import React, { useCallback, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, 
  X, 
  ArrowLeft,
  Filter,
  ChevronDown,
  Calendar,
  Clock,
  ArrowRight,
  BookOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ShareMenu } from '../components/ShareMenu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Header from '../components/Header';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getYoutubeVideoId(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = trimmed.match(re);
    if (m) return m[1];
  }
  return null;
}

function getPostThumbnailUrl(post) {
  if (post.featured_image_url) {
    return post.featured_image_url.startsWith('/') ? `${BACKEND_URL}${post.featured_image_url}` : post.featured_image_url;
  }
  const videoId = getYoutubeVideoId(post.youtube_url);
  if (videoId) return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  return null;
}

const FeaturedPost = ({ post }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const isIndustrialAIReport = (post?.title || '').toLowerCase().includes('industrial ai');

  const getReadTime = (content) => {
    if (!content) return '5 min read';
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const mins = Math.ceil(words / 200);
    return `${mins} min read`;
  };

  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/insights/${post.slug}` : '';

  return (
    <div className="group block relative">
      <Link 
        to={`/insights/${post.slug}`}
        className="block relative"
        data-testid={`featured-post-${post.slug}`}
        id={isIndustrialAIReport ? 'industrial-ai-report' : undefined}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#211F1F] to-[#3d3a3a]"
        >
          <div className="grid lg:grid-cols-2 gap-0">
            {/* Image */}
            <div className="relative aspect-[16/10] lg:aspect-auto lg:h-full overflow-hidden">
              <img
                src={getPostThumbnailUrl(post) || 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800'}
                alt={post.featured_image_alt || post.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent lg:hidden" />
            </div>

            {/* Content */}
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4">
                <Badge className="bg-[#C8473A] text-white hover:bg-[#C8473A]">
                  Featured
                </Badge>
                <span className="text-muted-on-dark text-sm">
                  {post.category}
                </span>
              </div>

              <h2 className="text-2xl lg:text-3xl xl:text-4xl font-bold text-white mb-4 group-hover:text-[#C8473A] transition-colors line-clamp-3">
                {post.title}
              </h2>

              {post.excerpt && (
                <p className="text-gray-300 mb-6 line-clamp-2 text-base lg:text-lg">
                  {post.excerpt}
                </p>
              )}

              <div className="flex items-center gap-4 text-sm text-muted-on-dark">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  {formatDate(post.published_at)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  {getReadTime(post.content)}
                </span>
              </div>

              <div className="mt-6 flex items-center gap-2 text-white group-hover:text-[#C8473A] transition-colors">
                <span className="font-medium">Read Article</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </motion.div>
      </Link>
      <div className="absolute top-4 right-4 z-10">
        <ShareMenu
          url={shareUrl}
          title={post.title}
          description={post.excerpt}
          triggerClassName="bg-white/90 hover:bg-white text-[#211F1F] border-0"
        />
      </div>
    </div>
  );
};

const PostCard = ({ post, index }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const shareUrl = typeof window !== 'undefined' ? `${window.location.origin}/insights/${post.slug}` : '';
  const isIndustrialAIReport = (post?.title || '').toLowerCase().includes('industrial ai');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="group h-full relative"
    >
      <Link
        to={`/insights/${post.slug}`}
        className="block h-full"
        data-testid={`post-card-${post.slug}`}
        id={isIndustrialAIReport ? 'industrial-ai-report' : undefined}
      >
        <article className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#C8473A]/20 hover:shadow-xl transition-all duration-300 h-full flex flex-col">
          {/* Image */}
          <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
            <img
              src={getPostThumbnailUrl(post) || 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=600'}
              alt={post.featured_image_alt || post.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=600';
              }}
            />
            {post.is_featured && (
              <div className="absolute top-3 left-3">
                <Badge className="bg-[#C8473A] text-white">Featured</Badge>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-5 flex-1 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="outline" className="text-xs font-medium">
                {post.category || 'General'}
              </Badge>
              <span className="text-xs text-muted-light">
                {formatDate(post.published_at)}
              </span>
            </div>

            <h2 className="heading-card-title group-hover:text-[#C8473A] transition-colors mb-2 line-clamp-2">
              {post.title}
            </h2>

            {post.excerpt && (
              <p className="text-sm text-gray-500 mb-4 line-clamp-2 flex-1">
                {post.excerpt}
              </p>
            )}

            {/* Tags */}
            {post.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-auto pt-3 border-t border-gray-50">
                {post.tags.slice(0, 3).map((tag, idx) => (
                  <span 
                    key={idx} 
                    className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}
                {post.tags.length > 3 && (
                  <span className="text-xs text-muted-light">+{post.tags.length - 3}</span>
                )}
              </div>
            )}
          </div>
        </article>
      </Link>
      <div className="absolute top-2 right-2 z-10">
        <ShareMenu
          url={shareUrl}
          title={post.title}
          description={post.excerpt}
          triggerClassName="bg-white/90 hover:bg-white text-gray-700 border-0 shadow-sm"
        />
      </div>
    </motion.div>
  );
};

const InsightsPage = () => {
  usePageTitle('Insights');
  const [posts, setPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (selectedTag !== 'all') params.append('tag', selectedTag);

      const [postsRes, featuredRes, filtersRes] = await Promise.all([
        axios.get(`${API}/admin/blog/public/list?${params.toString()}`),
        axios.get(`${API}/admin/blog/public/featured`),
        axios.get(`${API}/admin/blog/public/filters`)
      ]);
      
      setPosts(postsRes.data);
      setFeaturedPosts(featuredRes.data);
      setFilters(filtersRes.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedTag]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (loading) return;
    if (typeof window === 'undefined') return;
    if (window.location.hash !== '#industrial-ai-report') return;

    const el = document.getElementById('industrial-ai-report');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, posts, featuredPosts]);

  useEffect(() => {

    const description =
      "Insights from YourNest on building transformational DeepTech companies, market shifts and lessons from our founders and partners.";

    const existing = document.querySelector('meta[name="description"]');
    if (existing) {
      existing.setAttribute('content', description);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = description;
      document.head.appendChild(meta);
    }
  }, []);

  // Search filter (client-side for quick response)
  const filteredPosts = posts.filter(post => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      post.title?.toLowerCase().includes(search) ||
      post.excerpt?.toLowerCase().includes(search) ||
      post.tags?.some(t => t.toLowerCase().includes(search)) ||
      post.category?.toLowerCase().includes(search)
    );
  });

  // Separate featured from regular posts
  const mainFeatured = featuredPosts[0];
  const regularPosts = filteredPosts.filter(p => p.post_id !== mainFeatured?.post_id);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedTag('all');
  };

  const hasActiveFilters = searchTerm || selectedCategory !== 'all' || selectedTag !== 'all';

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      
      <main id="main-content" tabIndex={-1} className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <Link 
              to="/"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-[#C8473A] mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to Home
            </Link>
            
            <h1 className="text-4xl md:text-5xl font-bold text-[#C8473A] mb-4">
              Insights
            </h1>
            <p className="text-lg text-gray-600">
              Our perspectives on building transformational companies, industry trends, 
              and insights from the YourNest ecosystem.
            </p>
          </motion.div>

          {/* Featured Post */}
          {!hasActiveFilters && mainFeatured && (
            <div className="mb-12">
              <FeaturedPost post={mainFeatured} />
            </div>
          )}

          {/* Search & Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-8"
          >
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <label htmlFor="insights-search" className="sr-only">
                  Search insights posts
                </label>
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-light pointer-events-none" aria-hidden />
                <Input
                  id="insights-search"
                  placeholder="Search posts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-11 text-base rounded-xl border-gray-200"
                  data-testid="insights-search"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={`h-11 px-5 rounded-xl gap-2 ${showFilters ? 'bg-gray-50' : ''}`}
                data-testid="toggle-filters"
                aria-expanded={showFilters}
                aria-controls="insights-filters"
                aria-label={hasActiveFilters ? 'Show or hide insights filters (filters active)' : 'Show or hide insights filters'}
              >
                <Filter className="w-4 h-4" aria-hidden="true" />
                Filters
                {hasActiveFilters && (
                  <span className="w-2 h-2 rounded-full bg-[#C8473A]" aria-hidden="true"></span>
                )}
                <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} aria-hidden="true" />
              </Button>
            </div>

            {/* Filter Options */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  id="insights-filters"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                  aria-label="Insights filters"
                >
                  <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100 mt-4">
                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                      <SelectTrigger data-testid="filter-category" className="h-10 w-[180px]">
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {filters?.categories?.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select value={selectedTag} onValueChange={setSelectedTag}>
                      <SelectTrigger data-testid="filter-tag" className="h-10 w-[180px]">
                        <SelectValue placeholder="Tag" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Tags</SelectItem>
                        {filters?.tags?.map(tag => (
                          <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {hasActiveFilters && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearFilters}
                        className="text-gray-500 hover:text-[#C8473A] h-10"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Results Count */}
          <div className="flex items-center gap-2 mb-6 px-1">
            <BookOpen className="w-4 h-4 text-muted-light" />
            <p className="text-sm text-gray-500">
              <span className="font-medium text-[#211F1F]">{filteredPosts.length}</span> posts
            </p>
          </div>

          {/* Posts Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
                  <div className="aspect-[16/10] bg-gray-200"></div>
                  <div className="p-5">
                    <div className="flex gap-2 mb-3">
                      <div className="h-5 bg-gray-200 rounded w-20"></div>
                      <div className="h-5 bg-gray-100 rounded w-24"></div>
                    </div>
                    <div className="h-6 bg-gray-200 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-100 rounded w-3/4 mb-4"></div>
                    <div className="flex gap-2">
                      <div className="h-5 bg-gray-100 rounded-full w-16"></div>
                      <div className="h-5 bg-gray-100 rounded-full w-16"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <BookOpen className="w-14 h-14 mx-auto text-gray-300 mb-4" />
              <h2 className="heading-empty">No posts found</h2>
              <p className="text-gray-500 mb-6">Try adjusting your search or filters</p>
              <Button variant="outline" onClick={clearFilters}>
                Clear all filters
              </Button>
            </motion.div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="posts-grid">
              {regularPosts.map((post, index) => (
                <PostCard key={post.post_id} post={post} index={index} />
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default InsightsPage;
